import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, MailX, PenLine, Pencil, Plus } from "lucide-react";
import RecruitmentShell from "../components/RecruitmentShell";
import RecruitmentProfileSettingsNav from "../components/RecruitmentProfileSettingsNav";
import RejectionResponsePreviewModal from "../components/RejectionResponsePreviewModal";
import { useRejectionReasonTemplates } from "../hooks/useRejectionReasonTemplates";
import { useResponseSignature } from "../hooks/useResponseSignature";
import { sortRejectionReasonTemplates } from "../data/rejectionReasonTemplates";
import { resolveHiringCompanyName } from "../lib/responseSettings";

const SIGNATURE_EDIT_PATH = "/response-settings/signature";

function rejectionReasonEditPath(templateId) {
  return `/response-settings/rejection/${encodeURIComponent(templateId)}`;
}

export default function ResponseSettingsPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const { templates, addCustomReason, loading } = useRejectionReasonTemplates(authToken);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);
  const [addingReason, setAddingReason] = useState(false);
  const companyName = resolveHiringCompanyName(currentUser);
  const { savedSignature } = useResponseSignature(authToken, companyName);

  const sortedTemplates = useMemo(() => sortRejectionReasonTemplates(templates), [templates]);

  const handleAddReason = async () => {
    if (addingReason) return;
    setAddingReason(true);
    try {
      const template = await addCustomReason();
      navigate(rejectionReasonEditPath(template.id));
    } finally {
      setAddingReason(false);
    }
  };

  const previewTitle = previewTemplate?.label?.trim() || "Untitled reason";

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main className="nd1-shell nd1-recruitment-profile-main jw-recruitment-main recruitment-profile-page response-settings-page">
        <div className="recruitment-profile-shell nd1-recruitment-profile-shell">
          <header className="recruitment-profile-masthead nd1-recruitment-profile-masthead">
            <Link className="back-link nd1-recruitment-profile-back" to="/hiring-dashboard">
              &larr; Back to recruitment dashboard
            </Link>
            <p className="eyebrow nd1-label nd1-recruitment-profile-eyebrow">Your organization</p>
            <h1>Response settings</h1>
            <p className="subtitle">
              Templates for applicant notifications — including rejection messages.
            </p>
            <RecruitmentProfileSettingsNav active="response" />
          </header>

          <div className="recruitment-profile-content profile-manage-card nd1-recruitment-profile-content">
            <div className="recruitment-profile-stack recruitment-profile-stack--settings response-settings-stack">
              <section
                className="recruitment-profile-surface response-settings-signature-section"
                aria-labelledby="response-settings-signature-heading"
              >
                <p className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading" id="response-settings-signature-heading">
                  <PenLine className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Signature
                </p>
                <ul className="response-settings-reason-list">
                  <li className="response-settings-reason-row">
                    <Link to={SIGNATURE_EDIT_PATH} className="response-settings-reason-row__body">
                      <span className="response-settings-reason-row__label">Signature</span>
                    </Link>
                    <div
                      className="response-settings-reason-row__actions"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="response-settings-reason-row__icon-btn"
                        onClick={() => setSignaturePreviewOpen(true)}
                        aria-label="Preview signature"
                      >
                        <Eye size={18} strokeWidth={1.75} aria-hidden />
                      </button>
                      <Link
                        to={SIGNATURE_EDIT_PATH}
                        className="response-settings-reason-row__icon-btn"
                        aria-label="Edit signature"
                      >
                        <Pencil size={18} strokeWidth={1.75} aria-hidden />
                      </Link>
                    </div>
                  </li>
                </ul>
              </section>

              <section
                className="recruitment-profile-surface response-settings-rejection-section"
                aria-labelledby="response-settings-rejection-heading"
              >
                <p className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading" id="response-settings-rejection-heading">
                  <MailX className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                  Rejection notifications
                </p>
                <p className="profile-manage-helper response-settings-rejection-intro">
                  Preview or edit the message sent for each rejection reason.
                </p>

                <button type="button" className="response-settings-add-reason nd1-recruitment-settings-add-btn" onClick={handleAddReason}>
                  <Plus size={16} strokeWidth={1.75} aria-hidden />
                  Add new rejection reason
                </button>

                <ul className="response-settings-reason-list">
                  {sortedTemplates.map((template) => {
                    const label = template.label.trim() || "Untitled reason";
                    return (
                      <li key={template.id} className="response-settings-reason-row">
                        <Link
                          to={rejectionReasonEditPath(template.id)}
                          className="response-settings-reason-row__body"
                        >
                          <span className="response-settings-reason-row__label">{label}</span>
                        </Link>
                        <div
                          className="response-settings-reason-row__actions"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="response-settings-reason-row__icon-btn"
                            onClick={() => setPreviewTemplate(template)}
                            aria-label={`Preview message for ${label}`}
                          >
                            <Eye size={18} strokeWidth={1.75} aria-hidden />
                          </button>
                          <Link
                            to={rejectionReasonEditPath(template.id)}
                            className="response-settings-reason-row__icon-btn"
                            aria-label={`Edit response for ${label}`}
                          >
                            <Pencil size={18} strokeWidth={1.75} aria-hidden />
                          </Link>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>

      <RejectionResponsePreviewModal
        open={signaturePreviewOpen}
        onClose={() => setSignaturePreviewOpen(false)}
        title="Signature"
        responseHtml=""
        signature={savedSignature}
      />

      <RejectionResponsePreviewModal
        open={Boolean(previewTemplate)}
        onClose={() => setPreviewTemplate(null)}
        title={previewTitle}
        responseHtml={previewTemplate?.responseHtml}
        signature={savedSignature}
      />
    </RecruitmentShell>
  );
}
